import { ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AttachmentItem, useConversation, useConversationTranscript } from '@/entities/conversations';
import { useTimeDisplayFormat } from '@/entities/settings';
import { useDialogSessionOptional } from '@/features/DialogSession';
import { cn, SCROLL_AREA_VIEWPORT_BLOCK } from '@/shared/lib';
import { formatPhraseTimeRange } from '../../lib/formatPhraseTimeRange';

type Props = {
  /** ID диалога, чьи вложения отображаются */
  conversationId: number;
};

/**
 * Таб «Вложения» в карточке диалога.
 *
 * Отображает список вложений из ответа GET /conversation/{id} с поддержкой
 * внешних ссылок и подписанных ссылок MinIO. При наличии phraseId — выводит
 * кликабельный таймстемп фразы для прокрутки транскрипта к ней.
 */
export const AttachmentsTab: FC<Props> = ({ conversationId }) => {
  const { t } = useTranslation('dialogs');
  const { data: conversation } = useConversation(conversationId);
  const { data: transcripts } = useConversationTranscript(conversationId);
  const dialogSession = useDialogSessionOptional();
  const timeDisplayFormat = useTimeDisplayFormat();

  const attachments = conversation?.attachments ?? [];

  const phrasesById = useMemo<Map<number, { startedAt: number; finishedAt?: number | null }>>(() => {
    const map = new Map<number, { startedAt: number; finishedAt?: number | null }>();
    if (!transcripts) return map;
    for (const phrase of transcripts) {
      map.set(phrase.id, { startedAt: phrase.startedAt, finishedAt: phrase.finishedAt });
    }
    return map;
  }, [transcripts]);

  const handleScrollToPhrase = useCallback(
    (phraseId: number) => {
      dialogSession?.scrollToTranscript(String(phraseId));
    },
    [dialogSession?.scrollToTranscript],
  );

  if (attachments.length === 0) {
    return (
      <div className="flex items-center justify-center p-6 text-muted-foreground">{t('attachmentsEmpty')}</div>
    );
  }

  return (
    <ScrollArea orientation="vertical" className={cn('flex-1 min-h-0 w-full', SCROLL_AREA_VIEWPORT_BLOCK)}>
      <div className="flex flex-col gap-3 p-4 w-full min-w-0">
        {attachments.map((attachment) => {
          const phraseInfo = attachment.phraseId != null ? phrasesById.get(attachment.phraseId) : undefined;
          const phraseTimeLabel = phraseInfo
            ? formatPhraseTimeRange(phraseInfo.startedAt, phraseInfo.finishedAt, timeDisplayFormat)
            : null;
          return (
            <AttachmentItem
              key={attachment.id}
              attachment={attachment}
              phraseTimeLabel={phraseTimeLabel}
              onScrollToPhrase={dialogSession ? handleScrollToPhrase : undefined}
            />
          );
        })}
      </div>
    </ScrollArea>
  );
};
